/**
 * Revise theme — shipped copy and capture manifest (v4).
 *
 * Every other marketing theme keeps its defaults inline in each component
 * (`hero.headline || DEFAULTS.headline`). This theme has one tenant and a lot
 * more copy than the others, so the defaults live together here instead of
 * being scattered across nine files. The override contract is identical: a
 * value set in `tenants.branding.marketing` wins, anything absent falls back
 * to what is written below, and the page renders correctly with the column
 * completely empty.
 *
 * Captures are static assets under `public/marketing/revise/`. They sit in
 * `public/marketing/**`, which vite.config.ts deliberately excludes from the
 * PWA precache — these are product screenshots that no tenant's installed app
 * should be carrying around.
 *
 * `*asterisks*` mark the accent clause in a headline (see lib parseEm).
 *
 * ## What v4 changed
 *
 * The page now leads with a lesson you can actually take rather than three
 * screenshots of one. That reordered everything: the demo is the hook, so the
 * hero lost its capture row and its two reassurance lines, Practice Labs moved
 * last of the three products, the six-panel report gallery became a switcher
 * inside Essay Labs, and the standalone "what's free" section folded into
 * Practice Labs as ruled notes — the giveaway reads better as part of the
 * product it belongs to than as a separate pitch.
 */
import type {
  MarketingCredits,
  MarketingHero,
  MarketingLab,
  MarketingLabKey,
  MarketingPricing,
  MarketingCTA,
  MarketingAnnounce,
  MarketingShot,
  MarketingShotPanel,
  MarketingBrowse,
} from '../../../context/TenantContext';

const SHOTS = '/marketing/revise';

/**
 * Where "the free NSC papers" live.
 *
 * Both the announce bar and the button closing Practice Labs point here, so
 * the giveaway has one destination wherever someone meets it.
 *
 * It is a PAGE now, not a filtered catalogue. It used to be
 * `/courses?q=Practice+Lab`, which worked but arrived badly: on an 812px phone
 * the first 792px of that page is "Browse Courses / accelerate your career
 * growth", a search box and two filter rows, none of which mentions NSC,
 * History or matric, and the six papers came out in enrolment order. This
 * matters more than it sounds because the announce bar is the funnel — the
 * plan is to ask the Department of Basic Education to circulate it, so for a
 * lot of people this link IS Kula Revise, met cold on a phone from a WhatsApp
 * forward.
 *
 * `/free` is that arrival built on purpose: the offer, the six papers in paper
 * and question order, a worked example of what one contains, and the answer to
 * the teacher's question. See pages/FreePapers.tsx.
 *
 * `FREE_PRACTICE_QUERY` stays: it is the string `isFreePracticeLab` matches on
 * below, so it still decides which courses are "the free papers" even though
 * nothing searches for it any more. Override `announce.href` and
 * `labs.practice.cta.href` in `tenants.branding.marketing` to repoint the two
 * links without touching this file.
 */
export const FREE_PRACTICE_QUERY = 'Practice Lab';
export const FREE_PRACTICE_HREF = '/free';

/**
 * Is this course one of the free NSC papers?
 *
 * Derived from the SAME string the link above filters on, so the block and the
 * link can never end up describing different sets.
 *
 * NOT `price === 0`, which is the obvious rule and the wrong one: a Study Lab
 * (Paper 1 Q1, Origins of the Cold War) is also free, so pricing returns seven
 * courses under a heading that says six. The papers are identified by what
 * they are, not by what they cost — and if one is ever sold, the heading
 * should not silently change with it.
 */
export const isFreePracticeLab = (title: string): boolean =>
  title.trim().toLowerCase().startsWith(FREE_PRACTICE_QUERY.toLowerCase());

/* ── announce bar ─────────────────────────────────────────────── */

/**
 * The giveaway is SIX Practice Labs, three on each paper, and each one is a
 * single source-based question from a real NSC paper — 2022, 2023 and 2024 are
 * all represented. It is not two labs and it is not two whole papers, which is
 * what "Both NSC Practice Labs" claimed here until Aug 2026. Count and unit
 * both matter: a student who arrives expecting two complete papers has been
 * told the wrong thing twice. Check the catalogue before changing the number.
 */
export const ANNOUNCE: MarketingAnnounce = {
  badge: 'Free',
  // "NSC exam questions" alone names no subject — NSC covers every matric
  // subject, not just this one. "History" is the word a skimmer actually
  // needs, on a bar that sits above every page including the ones with no
  // other clue what the site is for. "Matric" over "student" for the same
  // reason: it's the SA-specific word ("a matric" is the person, see the
  // /free giveaway copy below) that tells a South African reader exactly who
  // this is for, where "student" reads as generic as "NSC" alone did.
  text: 'Six real NSC History exam questions, free for every matric and every teacher in South Africa',
  // The papers themselves, not the section that describes them. The bar is the
  // offer, so it should hand over the thing rather than the pitch for it.
  href: FREE_PRACTICE_HREF,
};

/* ── hero ─────────────────────────────────────────────────────── */

export const HERO: MarketingHero = {
  claim: 'We think *Kula Revise* is the best way to prepare for matric History.',
  /* Was "Don't take our word for it. Try a lesson, right here." — true and
     well-argued, but it doesn't say what the product IS: a reader skimming
     the hero got "try a lesson" with no subject attached until the small
     claim line above it or the subtitle below spelled out History/matric,
     and at a glance neither reliably lands (the claim is set as a sentence,
     not a headline, and the subtitle is three products deep). The pun does
     both jobs the old headline's words did — "History" names the subject,
     "exam questions" names what you actually get — in the one line that's
     guaranteed to be read. It also now matches the OG card
     (scripts/og/revise-home.html), so what gets promised in a shared link is
     what the page says when it opens.
     The `\n` is a hard break (see Hero.tsx's renderHeadline) — without it
     `text-wrap: balance` was free to wrap wherever it liked and stranded
     "So" on the first line, splitting the setup from its payoff. Placed at
     the sentence boundary so the `*emphasis*` clause starts clean on its
     own line rather than breaking mid-run. */
  headline: 'History repeats itself.\n*So do the exam questions.*',
  /* The three lines read top to bottom, so each one has to hand off to the
     NEXT — "Don't take our word for it" used to open this line because it
     directly rebutted the claim ("we think X is best" / "don't take our
     word for it") when the two sat back to back. Now the pun sits between
     them, so that rebuttal was answering a line the reader had already
     moved past two paragraphs ago — reading the claim then this subtitle
     back to back skips the headline entirely and still makes sense on its
     own, which is exactly the tell that this paragraph had stopped
     responding to what's actually above it. This version answers the
     headline instead ("so do the exam questions" → "try one, right here"),
     so all three lines read as one continuous argument in order. */
  subtitle:
    'Try one, right here. *Study Labs* take one topic all the way to the exam, *Essay Labs* read the essay you actually wrote and come back with the feedback a good teacher would give, and *Practice Labs* turn the real NSC papers into something you work through.',
  ctaText: 'Try a lesson free',
  // Down the page, not off it. The whole argument here is that you can judge
  // this yourself before giving anyone anything, and the next thing down is
  // Study Labs with the try-it-here lesson inside it. Use `#try` instead to
  // land directly on the lesson rather than on the section that introduces it.
  ctaHref: '#study',
};

/* ── the board choice, in the hero ────────────────────────────────
 *
 * Matric History is written under two boards, and a learner sits exactly one
 * of them — so the FIRST decision the page asks for is the board, at equal
 * weight (Dylan, 1 Sep): claim, headline, then two big cards. Everything
 * NSC-flavoured that used to live in the hero (the three-product subtitle,
 * "Try a lesson free") moved into the `#nsc` block directly below it, which
 * is where the NSC card scrolls to; the IEB card leaves for /ieb. Neither
 * board is "the page's" any more — the page below the choice is simply where
 * the NSC argument happens to live.
 *
 * The two descriptions are deliberately parallel in shape and length: any
 * asymmetry here reads as a ranking, which is the thing this block exists to
 * avoid. Free stays on the NSC line — it is true, it matters to the funnel,
 * and the IEB line carries its own range instead of an apology.
 */
/* Each card carries its OWN action (Dylan, 1 Sep): a shared "Try a lesson
 * free" button under both cards was a trap — an IEB candidate could click it
 * before choosing and land in the NSC demo. Now the try-hook lives on the
 * NSC door (straight to `#try`, the demo inside Study Labs) and the IEB door
 * leaves for /ieb, so neither board can mis-click the other's path. The
 * hero's old subtitle-and-button block is gone with it. */
export const BOARD_CHOICE = {
  kicker: 'Which exam are you writing?',
  nsc: {
    word: 'NSC',
    line: 'The national exam. Real past papers rebuilt as free Practice Labs.',
    go: 'Try a lesson free',
    href: '#try',
  },
  ieb: {
    word: 'IEB',
    line: 'The independent exam. Study, Skills and Essay Labs, built to its syllabus.',
    go: 'See the IEB range',
    href: '/ieb',
  },
} as const;

/* ── the three labs ───────────────────────────────────────────── */

export const LABS: Record<MarketingLabKey, MarketingLab> = {
  study: {
    eyebrow: 'Study Labs',
    heading: 'Not notes to read. *A topic, taken all the way to the exam.*',
    lede:
      "Twelve Study Labs, six on each paper. A quick quiz to show what you actually know, five short lessons that make you use the topic as you go, and by the end you've planned a real 50-mark essay on it, or a paragraph answer.",
    demo: {
      tag: 'Free to try, right now',
      // No title, and one line of body. The hero already says "try a lesson,
      // right here" and the section head says what a Study Lab is; repeating
      // either here is what made this block read as three introductions
      // stacked on top of each other. This line earns its place by saying
      // something neither has: how long it takes, and that it is a short
      // version of something longer.
      body:
        'This is how a Study Lab lesson opens — its first six steps, two or three minutes. The full one runs to nineteen.',
    },
    topicsSummary: 'All twelve topics, six per paper',
    topics: [
      { id: 'p1-cold-war-origins', track: 'Paper 1', title: 'Origins of the Cold War' },
      { id: 'p1-angola', track: 'Paper 1', title: 'Independent Africa: Angola' },
      { id: 'p1-civil-rights', track: 'Paper 1', title: 'Civil society protests: the US Civil Rights Movement' },
      { id: 'p1-vietnam', track: 'Paper 1', title: 'The Cold War: Vietnam' },
      { id: 'p1-congo', track: 'Paper 1', title: 'Independent Africa: the Congo' },
      { id: 'p1-black-power', track: 'Paper 1', title: 'The Black Power Movement' },
      { id: 'p2-crisis-apartheid', track: 'Paper 2', title: 'Civil resistance, 1970s to 1980s: the crisis of apartheid' },
      { id: 'p2-trc', track: 'Paper 2', title: 'Coming to terms with the past: the Truth and Reconciliation Commission' },
      { id: 'p2-globalisation', track: 'Paper 2', title: 'Globalisation and the new world order' },
      { id: 'p2-black-consciousness', track: 'Paper 2', title: 'Civil resistance, 1970s to 1980s: Black Consciousness' },
      { id: 'p2-negotiated-settlement', track: 'Paper 2', title: 'The coming of democracy: the negotiated settlement' },
      { id: 'p2-end-of-cold-war', track: 'Paper 2', title: 'The end of the Cold War and a new world order' },
    ],
  },

  essay: {
    eyebrow: 'Essay Labs',
    heading: 'Write the essay. *Get it back with the one thing to fix.*',
    lede:
      /* The asks sentence is shared with the IEB section (see IEB_PAGE.essay):
         the report is not the end of the Essay Lab on either board. The
         COUNTS are not shared — the NSC keeps two questions and four
         attempts where the IEB has one and three. */
      'Photograph your handwritten pages or upload it typed. It gets read three times, then comes back like a good teacher sat down with it: the one thing to fix, notes pinned to your own sentences, and an honest sense of where it sits. Then it stays open: try the fix yourself and hear whether it landed, ask to see one way of doing it, or ask a question about the report. Six Essay Labs, one for each essay slot on the two papers. Two questions each, four attempts.',
    solo: {
      shot: {
        src: `${SHOTS}/essay-pages.jpg`,
        alt: 'A photographed handwritten page beside the transcript it produced',
        label: 'Essay Lab',
        width: 1698,
        height: 838,
        density: 2,
      },
      caption:
        'Your actual handwriting, a page at a time, with the transcript beside it to check before anything is read. Real matric essays are written by hand, so that is how these get read too.',
    },
  },

  practice: {
    eyebrow: 'Practice Labs',
    heading: "The real papers, *like you've never seen them before.*",
    lede:
      'A past paper is a PDF and a memo you mark yourself, badly, and learn almost nothing from. That is a waste of the best revision material in the country. So we took the official NSC papers, exactly as they were set, and rebuilt the way you work them: the reason behind every answer, the guide for whatever skill you are stuck on, and marks split by the kind of thinking each question asked for.',
    /* One standing statement, no paragraphs under it.
     *
     * Both notes were arguments for the giveaway — that it is free full stop
     * rather than free to try, and that teachers get the same thing with no
     * form to fill in. The section around them already makes that case: the
     * lede says the papers were rebuilt, every capture is badged Free, and the
     * button below says "Free account, no card". The paragraphs were the same
     * point a third and fourth time. What survives is the claim itself. */
    notes: [{ position: 'before', label: 'Free for everybody, always' }],
    /* No closing button. This section used to carry one, from the v5
     * reference, back when it was the only route to the free papers besides
     * the nav. The browse window further down now shows all six as real cards
     * you can click, so a button saying "open a Practice Lab" sat above the
     * thing it was describing. The announce bar still routes here from every
     * page. `MarketingLabCta` stays registered so a tenant can put one back. */
  },
};

/* ── the browse window ────────────────────────────────────────── */

/**
 * A slice of `/courses`, on the landing page, using the catalogue's OWN card.
 *
 * Deliberately not a bespoke dark card: the point is that this is a window
 * into the real shelf, and a second design of the same object would be a
 * second thing to keep in sync for no gain. The cards are light, so the block
 * sits on a light surface — the same move the demo lesson makes, and the
 * reason it doesn't read as stranded.
 *
 * It shows the six free papers rather than a top-N of the catalogue. A raw
 * slice is ordered by `total_students`, so its contents would reshuffle as
 * students enrolled — a marketing page whose shelf changes on its own.
 *
 * It carries a heading and nothing else, because it is the TAIL of the
 * Practice Labs section above rather than a section of its own (see
 * `joined` in BrowseWindow.tsx). It used to open with its own eyebrow, its
 * own "Six real exam questions." and a lede restating what the section above
 * had just spent four panels demonstrating — the reader met the same offer
 * twice in a row and the second telling was the weaker one. What is left is
 * the line that does work the panels can't: the instruction to go and open
 * one.
 *
 * `heading` is load-bearing beyond its text — the window renders nothing at
 * all without it, so emptying it would take the six cards with it.
 */
export const BROWSE: MarketingBrowse = {
  heading: '*Open any of them now.*',
  ctaLabel: 'Browse everything',
  ctaHref: '/courses',
};

/**
 * The bundles window — the same block as BROWSE, with the packs behind it,
 * sitting straight after the hero.
 *
 * No closing button on purpose. Each card already links to its own
 * `/bundle/:id`, the page runs on into Study Labs immediately below, and the
 * courses window further down already carries "Browse everything" — a second
 * identical button this high would just be the same door twice.
 */
export const BUNDLES: MarketingBrowse = {
  eyebrow: 'Buy by the paper',
  heading: 'Everything for one paper, *in a single pack.*',
  lede:
    'The labs grouped the way the exam is: one paper at a time, source-based and essay work together. Buying a pack is cheaper than the labs inside it one by one.',
};

/* ── pricing ──────────────────────────────────────────────────── */

/**
 * These three figures MIRROR the tenant's bundle prices; nothing reads them
 * from the bundles, so a price change in /admin/bundles is a hand edit here
 * too (they drifted once: the bundles went to R350/R350/R629 and 65 days
 * while this block still said R499/R499/R899 and a year).
 *
 * R629 is deliberately below the two papers bought separately (R350 + R350 =
 * R700), so there is no route to the whole catalogue that beats it. Eighteen
 * labs at R99 is R1,782, which is why "about a third" holds.
 */
export const PRICING: MarketingPricing = {
  heading: 'One lab, one paper, *or the lot.*',
  /* "NSC lab", not "lab": this block is the NSC's flat R99, and the IEB
     prices its Study Labs at R129 (see IEB_PAGE.pricing). Unqualified, the
     sentence reads as a platform-wide claim and contradicts the IEB page and
     the printed IEB flyer. */
  subhead:
    'Every NSC lab is the same price, R99, whether it is a Study Lab or an Essay Lab. Buy them one at a time, buy everything for one paper, or buy the whole subject. A pack gives you 65 days of access from the day you buy it, enough to see you through the finals, and the NSC Practice Labs stay free either way.',
  plans: [
    {
      group: 'single',
      groupLabel: 'One at a time',
      name: 'Any single lab',
      price: 'R99',
      tagline:
        "Study Lab or Essay Lab, same price, no bundle to work out. Take the topic you are worried about, or the essay slot that keeps costing you marks. Eighteen labs to choose from, and a year's access to each one you take, for less than an hour with a tutor.",
      features: [],
      ctaLabel: '',
      ctaHref: '',
    },
    {
      group: 'paper',
      groupLabel: 'By the paper',
      name: 'Paper 1, prepared',
      price: 'R350',
      tagline: '',
      features: [
        'All six Paper 1 Study Labs',
        'All three Paper 1 Essay Labs',
        'Nine labs, R891 bought one at a time',
      ],
      ctaLabel: '',
      ctaHref: '',
    },
    {
      group: 'paper',
      name: 'Paper 2, prepared',
      price: 'R350',
      tagline: '',
      features: [
        'All six Paper 2 Study Labs',
        'All three Paper 2 Essay Labs',
        'The bigger paper, and the one most people leave late',
      ],
      ctaLabel: '',
      ctaHref: '',
    },
    {
      group: 'everything',
      groupLabel: 'Or the whole lot',
      name: 'Both papers',
      price: 'R629',
      tagline: '',
      featured: true,
      badge: 'Launch pricing for 2026',
      features: [
        'All twelve Study Labs and all six Essay Labs',
        'Less than the two papers bought separately',
        'About a third of what the eighteen labs cost one at a time',
        'The whole subject through to the finals, for less than a term of tutoring',
      ],
      ctaLabel: '',
      ctaHref: '',
    },
  ],
};

/* ── who made this ────────────────────────────────────────────── */

export const CREDITS: MarketingCredits = {
  eyebrow: 'Who made this',
  heading: 'Taught it, trained it, *and marked it.*',
  paragraphs: [
    'Kula Revise is built by two leading History educators, experienced across classroom teaching, tutoring and teacher training. Between them: thousands of South African History teachers trained, History and examination resources written for the national Department of Basic Education, and the Grade 12 History paper marked from the other side of the table.',
    'The team is led by a practising Cambridge A Level Lead Examiner in History. Students they have taught have gone on to universities including Oxford, Cambridge, Harvard, Yale, UCT and Stellenbosch, and come from leading schools in South Africa and globally.',
  ],
};

/* ── closing CTA ──────────────────────────────────────────────── */

export const CTA: MarketingCTA = {
  eyebrow: 'Start here',
  headline: 'Practice properly, *walk in ready.*',
  // No body. The whole page above it has made the case; a closing band that
  // re-argues it is a paragraph standing between someone and the button.
  // Says what it does, and says something the hero's button does not. Both ends
  // of the page read "Try a lesson free" until Aug 2026, but they go to
  // different places — the hero opens the lesson on this page, this one asks
  // for the account. Two identical labels pointing at two different doors is
  // the confusion `ctaHref` was added to remove, so the label has to differ too.
  buttonText: 'Sign up free',
  // NOT the hero's `#try`. Someone who has scrolled the whole page has already
  // passed the demo; sending them back up to it is a loop.
  //
  // `/signup` rather than `/courses` because the button asks for the account:
  // the tenant's own label here is "Sign up free", and a button that says sign
  // up and opens a catalogue has broken its promise. `/signup` is the Login
  // page in signup mode, which needs `selfRegistration` (default-on, and on
  // for this tenant) — with it off the page falls back to the login form,
  // which is still the right door for a returning student. The free papers
  // have their own two routes into the catalogue: the announce bar and the
  // button closing Practice Labs.
  ctaHref: '/signup',
};

/* ── footer ───────────────────────────────────────────────────── */

/**
 * The three-zone attribution rule, in its approved wording (12 Aug 2026).
 * Practice Labs may claim NSC/DBE; Study and Essay Labs must not. The
 * independence disclaimer stands alone as the third paragraph. Read all three
 * as a set before editing any one of them.
 *
 * Amended for the IEB launch (Dylan, 1 Sep 2026): the our-own-work paragraph
 * now covers the Skills Labs and both boards, and the independence paragraph
 * names the Independent Examinations Board beside the DBE. Paragraph one is
 * untouched — the Practice Labs remain NSC material and may keep saying so.
 */
export const LEGAL: string[] = [
  'The Practice Labs are built from past NSC examination papers, their addenda and their marking guidelines, published by the Department of Basic Education and freely available on its website. Each question is attributed to its year, paper and question number.',
  'The Study Labs, the Skills Labs and the Essay Labs are our own work. The content, the questions, the sources and the guidance behind the feedback are written by us, aligned to the CAPS curriculum each exam is set on and modelled on the kinds of questions each exam asks. They are not departmental or IEB material.',
  'Kula Revise is an independent product. It is not affiliated with, endorsed by or approved by the Department of Basic Education or the Independent Examinations Board.',
];

export const FOOTER_LINKS: Array<{ label: string; href: string }> = [
  { label: 'Who made this', href: '#who' },
  { label: 'Returns policy', href: 'https://www.thinkshow.co/kula-revise-returns' },
];


/* ── the lab sequences ────────────────────────────────────────────
 *
 * Essay and Practice used to be a capture plus a tabbed switcher. Both are
 * now a numbered SEQUENCE of rendered panels — see `LabSequence.tsx` for the
 * why, and the `.rv-seq` block in app.css for the how.
 *
 * The strings live here because every other word on this page does. What is
 * deliberately NOT here is a shape for `branding.marketing` to override.
 * These are not marketing lines a tenant should be retuning: they are one
 * worked example of one real NSC question, and the parts have to agree with
 * each other — the source supports the answer, the answer earns the marks,
 * the marks explain the verdict. Exposing them field by field invites an
 * example that contradicts itself, which is worse than an example nobody can
 * edit without a deploy.
 *
 * Everything below is transcribed from the product, not written for the page.
 */

export const PRACTICE_SEQUENCE = {
  steps: [
    {
      n: '1',
      title: 'The paper, the question, your answer, the reason',
      note: 'one screen, as it is in the product',
      source: {
        text: '…have been raised to pre-eminence and power far beyond their numbers and are seeking everywhere to obtain totalitarian control.',
        cite: 'Churchill, "Iron Curtain" speech, Fulton, Missouri, 5 March 1946',
      },
      question: {
        id: 'Q1.1.1',
        kind: 'Extraction',
        marks: '2 marks',
        text: "What, according to the source, was Churchill concerned about regarding the Soviet Union's intentions?",
        options: [
          { key: 'A', text: 'The unlimited and unknowable scope of Soviet expansive tendencies', correct: true },
          { key: 'B', text: 'Soviet plans to build atomic weapons by the end of 1946' },
          { key: 'C', text: 'Refusal to attend the new United Nations assemblies' },
        ],
      },
      reason: {
        heading: 'Correct, 2 marks earned',
        body: 'The source states "nobody knows what Soviet Russia… intend to do… or what are the limits, if any, to their expansive tendencies." Extraction = find the answer IN the text.',
      },
    },
    {
      n: '2',
      title: 'Stuck?',
      note: 'the coach opens over the paper',
      cue: 'Not sure how to find the answer? Tap here for extraction tips',
      steps: [
        { lead: 'Read the question first.', rest: 'Underline the key words.' },
        { lead: 'Scan for those words', rest: 'or their synonyms.' },
        { lead: 'Check the answer is IN the source.', rest: 'Two reasons means exactly two.' },
      ],
    },
    {
      n: '3',
      title: 'At the end',
      note: 'marks by kind of thinking, not out of 50',
      bars: [
        { name: 'L1 · Extraction & concept', score: '12 / 12', pct: 100, state: 'full' },
        { name: 'L2 · Interpretation & own knowledge', score: '22 / 22', pct: 100, state: 'full' },
        { name: 'L3 · Analysis, comparison, reliability', score: '8 / 16', pct: 50, state: 'part' },
      ],
      verdict: {
        lead: 'You can find it and you can explain it.',
        strong: 'What you are losing marks on is judging it',
        rest: '— reliability, and setting two sources against each other. That is the one to go and work on.',
      },
    },
    {
      n: '4',
      title: 'Score 70%',
      note: 'and then you do the marking',
      mark: {
        question: 'Does it use specific evidence from the sources?',
        options: ['Yes', 'Partial', 'No'],
        picked: 2,
      },
      miss: {
        heading: 'The answer is: Yes',
        body: 'It references Sources 1A, 1C and 1D with some relevant details. However, the evidence is presented as a sequence of separate facts rather than being analytically woven into a causal argument.',
      },
    },
  ],
  foot: 'Six real NSC questions, 2022 to 2024. Free for every student and every teacher in South Africa, with no card and no expiry.',
} as const;

export const ESSAY_SEQUENCE = {
  steps: [
    {
      n: '1',
      title: 'The one thing',
      note: 'not a list of everything wrong',
      quote: {
        parts: [
          "You state facts, but you don't ",
          { accent: 'explain' },
          " them. What's missing is the sentence after each fact that says ",
          { accent: 'why it pushed events the way it did' },
          '.',
        ],
      },
    },
    {
      n: '2',
      title: 'Your best line, and your own sentences',
      note: 'quoted back',
      bestLine: {
        heading: 'Your best line',
        body: '"The collapse of communist governments in Eastern Europe further weakened apartheid\'s international support."',
      },
      pinned: {
        before: '…in South Africa, ',
        highlight: 'although internal pressures also played an important role.',
        heading: '01 · Your introduction',
        body: 'Every comment is pinned to the exact sentence you wrote, with a suggested rewrite you can switch on and off.',
      },
    },
    {
      n: '3',
      title: 'Every paragraph, judged',
      note: 'and where the argument breaks',
      stat: {
        value: '6',
        label: 'of 9 paragraphs\nmove the argument',
        body: 'A marker reads for one thing: does this paragraph move your argument forward? The marks down the margin say which of yours did.',
      },
      pinned: {
        heading: 'Link · missing',
        body: 'Your fourth and fifth paragraphs are good material in the wrong place. Writing the link is left to you, which is the point.',
      },
    },
    {
      n: '4',
      title: 'Where it sits',
      note: 'honest about being rough',
      bands: [
        { name: 'Foundation' },
        { name: 'Developing' },
        { name: 'Solid middle', state: 'in' },
        { name: 'Upper', state: 'reach' },
      ],
      bandsBody:
        'Top of the Solid middle band, pushing at Upper. The full bar is the band it sits in; the short one is how far it reaches — another read could put it there. No pretend mark out of 50.',
    },
  ],
} as const;

/* ── page meta ────────────────────────────────────────────────── */

export const META = {
  title: 'Kula Revise · Grade 12 History · Try a Study Lab lesson, free',
  description:
    'Grade 12 History revision in three kinds of lab. Study Labs take a topic all the way. Essay Labs read the essay you wrote and come back with the feedback a good teacher would give. Practice Labs turn the real NSC papers into something you work through, free for every student and teacher in South Africa. Try a real Study Lab lesson right here on the page.',
};

/* ── /free, the giveaway page ─────────────────────────────────────
 *
 * The destination of the announce bar (see FREE_PRACTICE_HREF above) and, if
 * the Department circulates it, the first Kula Revise page a lot of people
 * ever see — cold, on a phone, forwarded on WhatsApp with no context.
 *
 * Every string is a code default like the rest of this file, but NOT
 * `branding.marketing`-overridable, for the reason the lab sequences aren't:
 * the page is one argument about one giveaway and the parts have to agree.
 * The hero says six, the papers section shows six, the teacher block prices
 * six at nothing. Exposing those field by field invites a page that
 * contradicts itself, and this is the page that must not.
 *
 * The counts are deliberately soft where they can be. The hero says "six"
 * because that is the offer being made and it has to be a number; everything
 * below counts whatever the catalogue actually returns. Publishing a seventh
 * paper is therefore a one-line edit here, not a hunt.
 *
 * The YEARS are named in exactly one place — each paper's own row, from its
 * own description. The hero says "the official past papers" and the teacher
 * block says "Official NSC questions, unaltered", so neither has to be
 * revisited when the set changes.
 */
export const FREE_PAGE = {
  meta: {
    title: 'Six real NSC exam questions, free · Kula Revise',
    description:
      'Six real NSC History exam questions, free for every student and every teacher in South Africa. The official past papers, exactly as they were set, rebuilt so you work them instead of marking yourself against a memo.',
  },

  hero: {
    kicker: 'Free · every student, every teacher in South Africa',
    headline: 'Six real NSC exam questions. *Yours, free.*',
    lede:
      'The official past papers, exactly as they were set, rebuilt so you work them, instead of marking yourself against a memo.',
    ctaLabel: 'Open Paper 1, Question 1',
    reassure: 'Free account, no card.',
  },

  papers: {
    kicker: 'All six, nothing held back',
    heading: 'Open *any of them* right now.',
    lede:
      'Three questions from each paper, drawn from three different years. Each one is a complete source-based question, not an extract of one.',
    /* Shown under a paper's heading. The count comes from the catalogue, so
       this is the unit rather than the number. */
    groupNote: (n: number) => `${n === 1 ? 'One question' : `${n} questions`}`,
  },

  /* The quiet bridge to the rest of the product, on a recessed band and
   * deliberately not under a heading — it is an aside, not a section someone
   * has to decide whether to enter.
   *
   * It sits directly under the six papers because that is the one moment the
   * reader has just seen what is on offer and not yet been asked for
   * anything. It is now the page's ONLY route to the rest of Kula Revise, and
   * it stays prose rather than becoming cards or a price: what stops an
   * official forwarding this is the feeling of having been sold to.
   *
   * The link is the words "look around" rather than a button after the
   * paragraph. The closing band used to carry it as "There is more to Kula
   * Revise →", which was wrong twice: an arrow reads as forward progress
   * while the destination is the homepage you can already reach from the
   * masthead, and sitting a few pixels above the real footer it looked like
   * chrome rather than an invitation. Inside the sentence that invites it,
   * the same link needs no arrow and no explanation.
   *
   * Rendered as before + link + after so the anchor can sit mid-sentence;
   * `parseEm` takes one wrapper, so it cannot do bold and a link at once.
   *
   * This block replaced the provenance sentence that used to sit here. The
   * attribution it carried is still on the page — the footer's three legal
   * paragraphs say what is departmental material and what is ours, which is
   * the version a teacher actually checks. */
  onward: {
    before: 'These six questions are the free corner of a much bigger library. When you are ready, ',
    linkLabel: 'look around',
    linkHref: '/',
    after:
      ': *Study Labs* take one topic all the way to a planned essay, *Essay Labs* read the essay you actually wrote and come back with the one thing to fix, and between the three of them you can prepare for every part of both papers.',
  },

  proof: {
    kicker: 'What you are actually opening',
    heading: 'A past paper is a PDF and a memo. *This is not that.*',
    lede:
      'One real question, start to finish — the source, what you answer, what comes back, and what your marks say about how you think.',
  },

  /* Addressed to whoever is holding the link, not to a job title.
   *
   * This was "For teachers and schools", written for the assumption that the
   * Department forwards to teachers and teachers forward to classes. That is
   * one path and not the widest one: a link travelling on WhatsApp reaches a
   * parent, an aunt, a older sibling, a matric's friend — none of whom have a
   * licence to arrange or a learner list to upload, and all of whom can help
   * one person revise. The institutional detail survives inside the points,
   * where a teacher will still find it; it just stopped being the headline.
   *
   * "A matric" is the person, not the year — standard South African usage and
   * the reason this heading reads as a favour rather than a product. */
  passOn: {
    kicker: 'Pass it on',
    heading: 'Help a matric revise. *For free.*',
    lede:
      'Send the link to anyone writing matric History — one learner, a whole class, your own child. Nothing to arrange, nothing to sign up for on your side. Every question here is free for them and free for you.',
    points: [
      { title: 'Nothing to install', body: 'It runs in a browser, on a phone or laptop.' },
      {
        title: 'One matric or a whole class',
        body: 'Thirty learners or three hundred — these six questions are free either way.',
      },
      { title: 'Drawn from the real papers', body: 'Official NSC questions, unaltered.' },
    ],
    /* The page spreads by being forwarded, so it hands over its own link
       rather than assuming someone copies the address bar. Resolved at render
       time from `window.location` — hardcoding the host would print the wrong
       one on every preview deploy and on localhost. */
    shareLabel: 'Copy link',
    shareDone: 'Copied',
  },

  cta: {
    kicker: 'Start here',
    headline: 'Start with *one question.*',
    /* One button and nothing else. The band used to carry a second, quieter
       link to the homepage; it now lives in `onward` above, inside the
       sentence that invites it. A closing band whose only action is "open a
       question" is the correct last thing this page says. */
    buttonLabel: 'Open Paper 1, Question 1',
  },
} as const;

/* ── /ieb, the IEB page ───────────────────────────────────────────
 *
 * The destination of the board strip under the landing hero, and the one page
 * an IEB candidate should need. The landing page stays the NSC's; this page
 * says what the IEB range is, shows it live off the catalogue, and asks for
 * the account.
 *
 * Like FREE_PAGE, every string is a code default and NOT
 * `branding.marketing`-overridable: the page is one argument about one range
 * and the parts have to agree with what the catalogue below them shows.
 *
 * No counts anywhere. The range is mid-publication — Study Labs are going
 * live one at a time and the Skills Labs and Essay Labs follow before launch
 * — so every number written here would be wrong within a week. The rows come
 * off the live catalogue and count themselves.
 *
 * Practice Labs are deliberately not mentioned. They are the NSC's — built
 * from NSC past papers — and this page describes what the IEB range HAS, not
 * what it lacks.
 */
/* ── the IEB launch gate ──────────────────────────────────────────
 *
 * OPEN since Monday 7 September 2026 (Dylan: "flip. we are live with IEB").
 *
 * The gate is kept rather than deleted: it is one boolean, it costs a
 * branch per door, and it is the only way to shut the page again quickly
 * if the range has to come down — which is a likelier need on a launch day
 * than on any other. Set it back to false and /ieb returns to a teaser
 * with every door closed and the launch pills back.
 *
 * With it true: topic chips link to their published courses, the shelf
 * cards are clickable, and the sign-up CTAs replace the pills. A chip
 * whose course is not published yet still renders as plain text, so the
 * page degrades topic by topic rather than all at once.
 */
export const IEB_LIVE = true;

export const IEB_PAGE = {
  meta: {
    title: 'Kula Revise for the IEB · Grade 12 History',
    description:
      'Grade 12 History revision built for the IEB. Study Labs take one topic all the way to the exam, Skills Labs coach the source and writing skills the paper rewards, and Essay Labs read the essay you actually wrote and give feedback worth acting on.',
  },

  hero: {
    kicker: 'IEB · Grade 12 History',
    /* Not "live here": the labs live in Kula Revise, the product — this page
       only describes them (Dylan, 1 Sep). */
    headline: 'Writing the IEB? *Your labs are ready.*',
    /* The Essay Labs clause must sell the feedback, not the reading — "read
       the essay you wrote" alone is a scanner, not a teacher (Dylan). */
    lede:
      'The whole IEB range in Kula Revise is built to your syllabus. Study Labs take one topic all the way to the exam, free Skills Labs coach the source and writing skills the paper rewards, and Essay Labs read the essay you actually wrote and come back with the feedback a good teacher would give.',
    ctaLabel: 'Sign up free',
    /* Claims only what the product does TODAY. An earlier draft promised
       "choose IEB when you sign up" — no such step exists (Dylan caught it,
       1 Sep). What is true: the account is free, and every lab in the IEB
       range is named IEB in the catalogue. If a board picker ever ships at
       signup, this is the line that gets to say so. */
    reassure: 'Free account, no card. Every lab in the IEB range is named IEB, so you always know which ones are yours.',
  },

  /* Shown wherever a door is shut while IEB_LIVE is false. */
  launch: {
    /* A named date, not "tomorrow" (Dylan, 4 Sep): a relative word is wrong
       the morning after it ships and nobody notices, where a date is either
       right or visibly stale. Both strings carry it, so the pill and the
       line under it can never disagree — and both change together. */
    pill: 'Launching Monday 7 September 2026',
    /* The pill sits immediately beside this and carries the date, so the
       line does NOT repeat it — it answers the other question instead:
       what am I looking at? */
    reassure: 'Everything below is what opens that day.',
  },

  /* The topic map — CURATED, not pulled (Dylan, 1 Sep). A live course list
   * advertised exactly how much of the range was published on any given day;
   * the map states the syllabus instead, the way the landing page's own
   * twelve-topic table does, and the catalogue is one click away via the CTA.
   *
   * Topics are grouped by THEME rather than by paper: the paper split is the
   * NSC page's organising idea, and pinning IEB topics to papers here would
   * assert something this file is not the authority on.
   *
   * `match` is a distinctive fragment of the topic's course title (see
   * findIebCourse in iebLabs.ts): the row links to its course the day the
   * course is published and is plain text until then. The topic set is drawn
   * from the IEB catalogue as built (Sep 2026) — confirm against the final
   * range before launch, and keep each `match` unique within the IEB titles.
   */
  map: {
    /* "Study Labs", not "The IEB range": the landing sections are kickered by
       product name (Study Labs / Essay Labs / Practice Labs) and this page's
       sections follow the same convention (Dylan, 1 Sep). */
    kicker: 'Study Labs',
    heading: 'One topic at a time, *taken all the way.*',
    lede:
      'Fourteen Study Labs, eight on the international section and six on the African. Each one takes a topic to exam readiness: a check of what you actually know, short lessons that make you use the topic as you go, and a real essay planned by the end.',
    /* The topic set, the wording and the two sections are the STUDENT FLYER's
       (KulaReviseStudentIEB.pdf, 4 Sep) — the printed page a learner may be
       holding while they read this one, so the two must agree topic for
       topic. Grouped international/African as the flyer groups them, not by
       theme: the earlier thematic grouping was a guess made before the flyer
       existed, and it also carried only eleven of the fourteen topics. */
    groups: [
      {
        label: 'International History',
        note: 'Eight topics',
        topics: [
          { title: 'Origins of the Cold War and the Berlin Crises', match: 'Berlin' },
          { title: 'The Cuban Missile Crisis', match: 'Cuban' },
          { title: 'Extension of the Cold War: China', match: 'China' },
          { title: 'Extension of the Cold War: Vietnam', match: 'Vietnam' },
          { title: 'The End of the Cold War', match: 'End of the Cold War' },
          { title: 'Globalisation and the New World Order', match: 'Globalisation' },
          { title: 'Civil Rights and Civil Society Protests', match: 'Civil Rights' },
          { title: 'Civil Society Protests: Black Power', match: 'Black Power' },
        ],
      },
      {
        label: 'African History',
        note: 'Six topics',
        topics: [
          { title: 'The Congo and Tanzania', match: ['Congo', 'Tanzania'] },
          { title: 'Independent Africa: Angola', match: 'Angola' },
          {
            title: 'Civil Resistance in the 1970s and 1980s: Black Consciousness',
            match: 'Black Consciousness',
          },
          {
            title: 'Civil Resistance, 1970s to 1980s: The Crisis of Apartheid in the 1980s',
            match: 'Crisis of Apartheid',
          },
          {
            title: 'The Coming of Democracy: The Negotiated Settlement',
            match: 'Negotiated Settlement',
          },
          {
            title: 'The Truth and Reconciliation Commission',
            match: ['Truth and Reconciliation', 'TRC'],
          },
        ],
      },
    ],
    /* Under the map, the shelf: the first few published IEB courses as the
       catalogue's OWN cards — thumbnails and all, the shelf someone will
       actually land on. Capped so it reads as a taste, not an inventory
       (the map above already states the full range), and rendered only once
       something is published — before launch the map closes the section on
       its own. */
    shelfCap: 3,
    shelfMore: (n: number) =>
      n === 1 ? '…and one more in the catalogue.' : `…and ${n} more in the catalogue.`,
    /* The scope parameter, not a text search: it puts the catalogue INTO the
       IEB curriculum (and remembers it), where `?q=IEB` only matched titles
       and left the reader in whichever curriculum they happened to be in. */
    cta: { label: 'Browse the IEB labs', href: '/courses?curriculum=IEB' },
  },

  /* The Skills Labs — the IEB's own free giveaway, promoted to a full
   * section (Dylan, 1 Sep): a lab-section head like Essay Labs' (small
   * product kicker, big statement), the landing's own free label verbatim,
   * and the chips on a brand-pink washed panel. The name is new to the
   * reader, so the lede says what a Skills Lab is before the chips name the
   * three. */
  skills: {
    kicker: 'Skills Labs',
    heading: 'Not more content. *The skills the exam actually marks.*',
    /* FOUR, and the flyer names all four — the source-based essay was missing
       from this list until the student flyer was checked against it (4 Sep). */
    lede:
      'Four short labs, not tied to any one topic: reading a single source, and building the source-based essay, the discursive essay and the extended writing response.',
    /* The landing Practice Labs' standing label, verbatim — same claim, same
       weight, same words. */
    freeLabel: 'Free for everybody, always',
    topics: [
      { title: 'Single-Source Analysis', match: 'Single-Source' },
      { title: 'How to Write the Source-Based Essay', match: 'Source-Based Essay' },
      { title: 'How to Write a Discursive Essay', match: 'Discursive' },
      { title: 'How to Write an Extended Writing Response', match: 'Extended Writing' },
    ],
  },

  essay: {
    kicker: 'Essay Labs',
    /* The landing page's Essay Labs section, word for word (Dylan, 1 Sep) —
       same heading, same lede, and the page reuses `LABS.essay.solo` for the
       handwriting capture so the picture stays single-sourced. The ONE cut is
       the lede's closing count — "Six Essay Labs, one for each essay slot on
       the two papers. Two questions each, four attempts." — which describes
       the NSC range and must not be re-added here. Everything else is the
       same product making the same promise. */
    heading: 'Write the essay. *Get it back with the one thing to fix.*',
    /* Two things here are IEB-specific and differ from the NSC section above,
       so do not copy either across:

       COUNTS. The IEB gets ONE question per lab and THREE attempts (Dylan,
       Sept 2026), where the NSC gets two and four. An earlier draft of this
       line carried the NSC's numbers, and the first student flyer printed
       two-and-four for the IEB as well; the flyer is being reissued.

       THE ASKS. The report is no longer the end of it — a learner can try
       the fix and hear whether it landed, ask to see one way of doing it, or
       ask a question about the report. That is the shipped Essay Lab (three
       asks per marking) and the page described a product that stopped at the
       report. It applies to both boards, so the NSC section is a candidate
       for the same sentence. */
    lede:
      'Photograph your handwritten pages or upload it typed. It gets read three times, then comes back like a good teacher sat down with it: the one thing to fix, notes pinned to your own sentences, and an honest sense of where it sits. Then it stays open: try the fix yourself and hear whether it landed, ask to see one way of doing it, or ask a question about the report. Six Essay Labs, one question in each and three attempts.',
  },

  /* IEB pricing — the student flyer's, figure for figure.
   *
   * It is NOT the NSC's: a Study Lab is R129 here and an Essay Lab R99,
   * where every NSC lab is R99 flat, and the sets are cut by section
   * (international / African) rather than by paper. The landing page's
   * PRICING block therefore says NSC explicitly, so a reader who sees both
   * cannot read one board's prices as the other's.
   *
   * The parts-totals are the flyer's and they check out against the unit
   * prices: 14×129 + 6×99 = 2 400; 8×129 + 4×99 = 1 428; 6×129 + 2×99 =
   * 972. Anything that changes a unit price changes all three totals with
   * it — that is the whole reason they are written down here.
   */
  pricing: {
    heading: 'Three sets, *or one lab at a time.*',
    subhead:
      'Every set gives you 65 days of access from the day you buy it, enough to see you through the finals, and the four Skills Labs stay free whether you buy anything or not.',
    plans: [
      {
        group: 'sets',
        groupLabel: 'The three sets',
        name: 'The Full Set',
        /* R899, mirroring the tenant's bundle (it was R1 299 until the
           sets were repriced for the run-in to the finals; the bundle now
           also carries four live Masterclasses).

           THE THREE SET PRICES ARE ONE DECISION, NOT THREE. This tier has
           to undercut its own two sections bought together, or the
           everything tier becomes the worst way to buy everything. The
           sections are 549 + 549 = R1 098, so the Full Set saves R199
           against the halves and R1 501 against singles. Change any one of
           the three and check the other two still stack. */
        price: 'R899',
        tagline: '',
        featured: true,
        badge: 'Launch pricing for 2026',
        features: [
          'All twenty labs: every Study Lab and every Essay Lab',
          'International and African, the whole syllabus',
          'Four live online Masterclasses, two for each paper',
          'R2 400 taken one at a time',
        ],
        ctaLabel: '',
        ctaHref: '',
      },
      {
        group: 'sets',
        name: 'International History Set',
        price: 'R549',
        tagline: '',
        features: [
          'Twelve labs: eight Study Labs and four Essay Labs',
          'The Cold War from Berlin to its end, Globalisation, Civil Rights and Black Power',
          'R1 428 one at a time',
        ],
        ctaLabel: '',
        ctaHref: '',
      },
      {
        group: 'sets',
        name: 'African History Set',
        price: 'R549',
        tagline: '',
        features: [
          'Eight labs: six Study Labs and two Essay Labs',
          'The Congo and Tanzania, Angola, civil resistance, the Negotiated Settlement and the TRC',
          'R972 one at a time',
        ],
        ctaLabel: '',
        ctaHref: '',
      },
      {
        group: 'single',
        groupLabel: 'Or one at a time',
        name: 'Any Study Lab',
        price: 'R129',
        tagline: 'One topic, taken all the way to a planned essay.',
        features: [],
        ctaLabel: '',
        ctaHref: '',
      },
      {
        group: 'single',
        name: 'Any Essay Lab',
        price: 'R99',
        tagline: 'One question, three attempts, read three times each.',
        features: [],
        ctaLabel: '',
        ctaHref: '',
      },
    ],
    /* The refund terms are the flyer's, and they belong wherever a price is
       shown. Kept as one quiet line rather than a tier feature — it is a
       condition of buying, not a reason to buy. */
    note: 'Every set gives you 65 days of access. You can cancel within fourteen days of buying, and if you have used Essay Lab attempts by then the refund is worked out on the attempts you have left.',
  },

  /* The try-it lesson, reused from the landing page rather than rebuilt.
     It replays the opening of Origins of the Cold War — which is the FIRST
     topic on the IEB's own international section, so the content is as
     right for this page as for the NSC one. The line below says which lab
     it is, because a demo that could be from any board is worth less to a
     reader who has just been told this page is built to their syllabus. */
  demo: {
    tag: 'Free to try, right now',
    body:
      'This is how a Study Lab opens — the first six steps of Origins of the Cold War and the Berlin Crises, one of your fourteen. Two or three minutes; the full lab runs to nineteen steps.',
  },

  /* The three sets as the catalogue's own cards, under the pricing that
     explains them. The tiers say what each set contains and what it saves;
     the cards are the ones you can actually click. Same split the landing
     page runs — it carries a bundle window AND a pricing section. */
  bundles: {
    eyebrow: 'Buy a set',
    heading: 'The sets, *ready to open.*',
    /* Four cards, not three: the Skills Labs ship as a free pack of their own
       and it sits on this shelf beside the paid sets, so the line has to
       account for it rather than say the Skills Labs are "not in any of
       them" with one of them on screen. */
    lede:
      'Each paid set is one purchase and 65 days of access. The Skills Labs pack is free, with no card and no expiry.',
  },

  cta: {
    kicker: 'Start here',
    headline: 'Practice properly, *walk in ready.*',
    buttonLabel: 'Sign up free',
    /* `/signup`, not `#top`: same reasoning as the landing page's closing
       band (see CTA above) — the button asks for the account. The curriculum
       parameter carries the board across sign-up: a reader who came from THIS
       page has said which exam they write, and the app remembers it (see
       ScopeContext / ScopeUrlSync) instead of dropping them onto the NSC
       default. The hero CTA reads the same href, so the two can't disagree. */
    href: '/signup?curriculum=IEB',
  },
} as const;

/* ── the school licence ───────────────────────────────────────────
 *
 * A school buys once and every Grade 12 History learner in it is covered.
 * It sits under the pricing on both boards because it is the same question
 * — what does this cost — answered for the other kind of buyer.
 *
 * The two boards price it DIFFERENTLY and the shapes are not
 * interchangeable: the NSC is banded on school fees so that the schools who
 * can carry more do, while the IEB is one flat price. Hence `bands` OR
 * `flat`, never both.
 *
 * Unlike a lab or a set, this cannot be bought on the platform — it is
 * arranged with a person. `SCHOOL_EMAIL` is therefore the one address the
 * marketing site publishes, kept in one place so it can become a
 * role address (schools@) without hunting through copy.
 */
export const SCHOOL_EMAIL = 'dylan@kulalearn.com';

/**
 * The button on a school licence block, when that board has a form.
 *
 * There is ONE FORM PER BOARD — they ask different things, because the NSC
 * is priced on a fee band and the IEB is a flat price — so the URL lives on
 * each board's own copy below (`formUrl`) rather than in a single constant.
 *
 * An empty `formUrl` falls back to the email line, so a board without a form
 * yet still has a working way in and the page is correct either way. That is
 * the IEB's state until its form exists.
 */
export const SCHOOL_FORM_LABEL = 'Ask about a school licence';

export interface ReviseSchoolCopy {
  coversEyebrow: string;
  covers: string;
  selfEyebrow: string;
  self: string;
  title: string;
  badge: string;
  lede?: string;
  bandsHead?: { left: string; right: string };
  bands?: ReadonlyArray<{ label: string; price: string }>;
  flat?: { price: string; body: string };
  note?: string;
  contact: string;
  /** This board's enquiry form. Empty falls back to the email line. */
  formUrl?: string;
}

export const SCHOOL_NSC: ReviseSchoolCopy = {
  coversEyebrow: 'The school covers everyone',
  covers:
    'One licence covers every Grade 12 History learner in your school until the end of the year. Every lab, both papers. No per-learner charge, no head count, and nothing left for families to buy separately.',
  selfEyebrow: 'Or learners can buy for themselves',
  self:
    'Individual labs start at R99, with bundles for a topic, a half paper and a whole paper. A learner signs up and pays on the platform. Nothing needs to go through the school, and nothing needs your time.',
  title: 'School licence',
  badge: 'Launch special',
  lede: 'Priced on your school fees, so that the schools who can carry more do.',
  bandsHead: { left: 'Annual school fees per learner', right: 'For the year' },
  bands: [
    { label: 'Under R20,000', price: 'R1,000' },
    { label: 'R20,000 to R40,000', price: 'R2,000' },
    { label: 'Over R40,000', price: 'R3,000' },
  ],
  note:
    'No-fee schools are covered separately. If your school does not charge fees, write to us and we will arrange access at no cost.',
  contact: 'School licences are arranged by email:',
  /* Stripped of the `?usp=publish-editor` query Google appends when the link
     is copied out of the editor — it carries the editor's context and has no
     business on a public page. Verified reachable without a Google sign-in,
     which is the thing that quietly breaks these: a form restricted to the
     org would send every school to a login screen. */
  formUrl:
    'https://docs.google.com/forms/d/e/1FAIpQLSduIbNoZUXhImkzZxLSzIcYvPT0hvxxNn_sNTjp4arp-osLPA/viewform',
};

export const SCHOOL_IEB: ReviseSchoolCopy = {
  coversEyebrow: 'The school covers everyone',
  covers:
    'One licence covers every Grade 12 History learner in your school until the end of the year. Every lab and every topic, with no per-learner charge and no head count.',
  selfEyebrow: 'Or learners can buy for themselves',
  self:
    'Study Labs are R129 and Essay Labs are R99, with three sets: the full syllabus at R899, and the international topics and the African topics at R549 each. They sign up and pay on the platform, so nothing needs to go through the school and nothing needs your time.',
  title: 'School licence',
  badge: 'Launch special',
  flat: {
    price: 'R3,999',
    body:
      'One price for the whole matric group, from now until the finals. The prelims are behind them and they are studying at home on their own, so this is what goes with them, and you know every one of them has it.',
  },
  contact: 'School licences are arranged by email:',
  /* The IEB's own form, not the NSC's: same contact questions but no fee
     band, because this board is one flat price. Same two checks as the NSC
     link — reachable without a Google sign-in, and stripped of the
     `?usp=publish-editor` query the editor appends. */
  formUrl:
    'https://docs.google.com/forms/d/e/1FAIpQLSdz5OVE-HPcv-MJqx4DT3sgbH3kc2uN5hfO0u5YXmtc0oBdMw/viewform',
};
